import { PriceLabelSimple } from '@/components/features/shared/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { UserRole } from '@/domain/auth/auth'
import { Offering } from '@/domain/offerings/offering'
import { useDeletePrice } from '@/hooks/api/useOfferings'
import { useAuthStore } from '@/store/useAuthStore'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { History, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Paths } from '@/routes/paths'
import { ViewAgePricingModal } from './ViewAgePricingModal'

interface OfferingPricesCardProps {
  offering: Offering
}

export const OfferingPricesCard = ({ offering }: OfferingPricesCardProps) => {
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null)
  const { hasAnyRole } = useAuthStore()
  const canManage = hasAnyRole([UserRole.Admin, UserRole.SuperAdmin])
  const { mutateAsync: deletePrice } = useDeletePrice()
  const navigate = useNavigate()

  const prices = offering.prices || []

  if (prices.length === 0 && !canManage) {
    return null
  }

  const handleDelete = async (priceId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este registro de precio?')) {
      await deletePrice({ offeringId: offering.id, priceId })
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Precios Programados</CardTitle>
        </div>
        {canManage && (
          <Button
            size="sm"
            onClick={() => navigate({ to: Paths.Management.NewOfferingPrice.replace('$offeringId', offering.id) as any })}
            disabled={offering.status !== 'active'}
            title={offering.status !== 'active' ? 'Solo se pueden agregar precios a servicios activos' : ''}
          >
            <Plus className="mr-2 h-4 w-4" />
            Agregar Precio
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {prices.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay precios programados disponibles.
          </p>
        ) : (
          <div className="relative overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-muted-foreground uppercase bg-muted/50">
                <tr>
                  <th className="px-4 py-2 font-medium">Precio</th>
                  <th className="px-4 py-2 font-medium">Desde</th>
                  <th className="px-4 py-2 font-medium">Hasta</th>
                  {offering.hasAgePricing && <th className="px-4 py-2 font-medium">Rangos</th>}
                  {canManage && <th className="px-4 py-2 font-medium text-right">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-muted">
                {prices.map((price) => (
                  <tr key={price.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium text-blue-600">
                      <PriceLabelSimple price={price.price} currency={price.currency} />
                    </td>
                    <td className="px-4 py-3 capitalize">
                      {format(new Date(price.effectiveFrom), 'dd MMMM yyyy', { locale: es })}
                    </td>
                    <td className="px-4 py-3 capitalize">
                      {price.effectiveUntil
                        ? format(new Date(price.effectiveUntil), 'dd MMMM yyyy', { locale: es })
                        : '-'}
                    </td>
                    {offering.hasAgePricing && (
                      <td className="px-4 py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() => setSelectedHistoryId(price.id)}
                        >
                          Ver Rangos
                        </Button>
                      </td>
                    )}
                    {canManage && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(price.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <ViewAgePricingModal
        offeringId={offering.id}
        priceId={selectedHistoryId}
        isOpen={!!selectedHistoryId}
        onClose={() => setSelectedHistoryId(null)}
      />
    </Card>
  )
}
